import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetProfileQueryKey,
  useUpdateUserPreferences,
} from "../generated/hook";
import { ViewPeriod } from "../components/ContributionHeatmap/ContributionHeatmap.service";
import useAuth from "./useAuth";

type PaletteType = "light" | "dark";

export const useUserPreferences = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fonction pour obtenir la préférence avec le bon typage
  const getUserPreferredViewPeriod = useCallback((): ViewPeriod => {
    // Accès temporaire avec assertion de type en attendant la mise à jour des types
    const userWithPrefs = user as any;
    return (userWithPrefs?.preferredViewPeriod as ViewPeriod) || "week";
  }, [user]);

  const getUserPaletteType = useCallback((): PaletteType => {
    const userWithPrefs = user as any;
    return (userWithPrefs?.paletteType as PaletteType) || "light";
  }, [user]);

  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>(
    getUserPreferredViewPeriod()
  );

  const [paletteType, setPaletteType] = useState<PaletteType>(
    getUserPaletteType()
  );

  // Mutation pour mettre à jour les préférences utilisateur
  const updatePreferencesMutation = useUpdateUserPreferences({
    mutation: {
      onSuccess: () => {
        // Invalider la requête de l'utilisateur connecté pour mettre à jour le cache
        queryClient.invalidateQueries({
          queryKey: getGetProfileQueryKey(),
        });
      },
      onError: (error) => {
        console.error("Failed to update user preferences:", error);
        // En cas d'erreur, revenir aux valeurs précédentes
        setViewPeriod(getUserPreferredViewPeriod());
        setPaletteType(getUserPaletteType());
      },
    },
  });

  // Synchroniser les préférences avec les données utilisateur
  useEffect(() => {
    const userViewPeriod = getUserPreferredViewPeriod();
    const userPaletteType = getUserPaletteType();

    if (userViewPeriod !== viewPeriod) {
      setViewPeriod(userViewPeriod);
    }

    if (userPaletteType !== paletteType) {
      setPaletteType(userPaletteType);
    }
  }, [
    user,
    viewPeriod,
    paletteType,
    getUserPreferredViewPeriod,
    getUserPaletteType,
  ]);

  const updateViewPeriod = (newViewPeriod: ViewPeriod) => {
    // Mise à jour optimiste de l'UI
    setViewPeriod(newViewPeriod);

    // Sauvegarder la préférence dans la base de données
    const preferencesData = {
      preferredViewPeriod: newViewPeriod,
    };

    updatePreferencesMutation.mutate({ data: preferencesData });
  };

  const updatePaletteType = (newPaletteType: PaletteType) => {
    // Mise à jour optimiste de l'UI
    setPaletteType(newPaletteType);

    // Sauvegarder la préférence dans la base de données
    const preferencesData = {
      paletteType: newPaletteType,
    };

    updatePreferencesMutation.mutate({ data: preferencesData });
  };

  return {
    viewPeriod,
    updateViewPeriod,
    paletteType,
    updatePaletteType,
    isUpdating: updatePreferencesMutation.isPending,
  };
};
